import React from 'react';

import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTheme } from '@material-ui/core/styles';

import StoreItemsList from './components/storeItems/StoreItemsList';
import StoreDrawer from './components/stores/StoreDrawer';
import ItemTypesDialog from './components/itemTypes/ItemTypesDialog';
import ArchivedStoreItemsDialog from './components/storeItems/ArchivedStoreItemsDialog';

import AddStoreProductDialog from '../factories/components/products/AddStoreProductDialog';

import AuditStoreDialog from './components/stores/AuditStoreDialog';
import ScanQrCode from './components/storeItems/actions/ScanQRCode';
import StoresLabelDialog from './components/storeItems/labels/StoreLabelsDialog';

import { storesMng } from './api/storesMng';
import GenerateReportsDialog from './components/storeItems/GenerateReportsDialog';

function Stores(props) {
  const {
    stores,
    storesLoading,
    storesError,
    storeItems,
    fetchStores, // this will result in the item list to update
    permissions,
    selectedStore,
    filters,
    onOpenStoreDialog,
    hideDrawerFab,
    onActionsReady,
    onAnalytics,
    onEditStore,
    onArchiveStore,
    onItemsLoaded,
  } = props;

  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';

  const [itemTypes, setItemTypes] = React.useState();
  const [openAddStoreItem, setOpenAddStoreItem] = React.useState(false);
  const [openAddStoreProduct, setOpenAddStoreProduct] = React.useState(false);
  const [selectedStoreItem, setSelectedStoreItem] = React.useState(undefined);
  const [storeDrawerOpen, setStoreDrawerOpen] = React.useState(false);
  const [openArchives, setOpenArchives] = React.useState(false);
  const [openAuditStoreDialog, setOpenAuditStoreDialog] = React.useState(false);
  const [openScanQrCode, setOpenScanQrCode] = React.useState(false);
  const [openExportStoreItems, setOpenExportStoreItems] = React.useState();
  const [openStoreLabelsDialog, setOpenStoreLabelsDialog] = React.useState();

  React.useEffect(() => {
    if (onActionsReady) {
      onActionsReady({
        addItem: () => setOpenAddStoreItem(true),
        addProduct: () => setOpenAddStoreProduct(true),
        auditStore: () => setOpenAuditStoreDialog(true),
        openArchives: () => setOpenArchives(true),
        exportToCsv: () => setOpenExportStoreItems(true),
        generateLabels: () => setOpenStoreLabelsDialog(true),
      });
    }
  }, [selectedStore]);

  const fetchItemTypes = async () => {
    const storeItemTypes = await storesMng.getStoreItemTypes(selectedStore._id);
    setItemTypes(storeItemTypes);
  };
  const deleteStoreItem = async () => {
    if (selectedStoreItem) {
      const result = await storesMng.deleteStoreItem(selectedStoreItem._id);
      fetchStores();
    }
  };

  const renderSkeleton = () => {
    const skFrom = isDark ? '#2a2a2e' : '#e2e8f0';
    const skTo = isDark ? '#3a3a40' : '#f1f5f9';
    const Sk = ({ w, h, r = 8, style = {}, delay = 0 }) => (
      <div style={{
        width: w, height: h, borderRadius: r,
        backgroundColor: skFrom,
        animation: `skPulse 2s ease-in-out ${delay}s infinite`,
        ...style,
      }} />
    );
    return (
      <div style={{ padding: '0' }}>
        <style>{`
          @keyframes skPulse {
            0%, 100% { background-color: ${skFrom}; }
            50% { background-color: ${skTo}; }
          }
        `}</style>
        {/* Items list skeleton */}
        <div style={{
          backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#fff',
          borderRadius: '16px',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
          margin: '8px 20px',
          overflow: 'hidden',
        }}>
          {[0, 0.06, 0.12, 0.18, 0.24, 0.3, 0.36, 0.42].map((d, i) => (
            <div key={i} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              padding: '14px 16px',
              borderBottom: i < 7 ? `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : '#f1f5f9'}` : 'none',
            }}>
              {/* Thumbnail */}
              <Sk w="70px" h="70px" r={12} delay={d} />
              {/* Content */}
              <div style={{ flex: 1 }}>
                <Sk w="55%" h="14px" r={4} delay={d + 0.03} />
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <Sk w="60px" h="10px" r={3} delay={d + 0.06} />
                  <Sk w="40px" h="10px" r={3} delay={d + 0.08} />
                  <Sk w="50px" h="10px" r={3} delay={d + 0.1} />
                </div>
              </div>
              {/* Quantity */}
              <div style={{ textAlign: 'right' }}>
                <Sk w="50px" h="18px" r={4} delay={d + 0.05} style={{ marginLeft: 'auto' }} />
                <Sk w="30px" h="10px" r={3} delay={d + 0.08} style={{ marginTop: 4, marginLeft: 'auto' }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderBody = () => {
    // Show error state if there's an error
    if (storesError) {
      return (
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          padding: '40px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
          <h3 style={{ marginBottom: '10px', color: '#d32f2f' }}>Error Loading Stores</h3>
          <p style={{ marginBottom: '20px', color: '#888' }}>
            {storesError}
          </p>
          <button
            onClick={() => {
              if (fetchStores) {
                fetchStores();
              }
            }}
            style={{
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Try Again
          </button>
        </div>
      );
    }
    
    // If stores is undefined or loading, show skeleton
    if (stores === undefined || storesLoading) {
      return renderSkeleton();
    }

    // If stores is an empty array, show empty state
    if (Array.isArray(stores) && stores.length === 0) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>🏪</div>
          <h3 style={{ marginBottom: '10px', color: '#666' }}>No Stores Found</h3>
          <p style={{ marginBottom: '20px', color: '#888' }}>
            You don't have any stores yet. Create your first store to get started.
          </p>
          <button
            onClick={() => {
              if (onOpenStoreDialog) {
                onOpenStoreDialog();
              }
            }}
            style={{
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Create Your First Store
          </button>
        </div>
      );
    }

    // If we have stores but no selected store, show skeleton
    if (!selectedStore) {
      return renderSkeleton();
    }
    return (
      <div className={'noselect'} style={{ marginBottom: '20px' }}>
        <StoreItemsList
          store={selectedStore}
          filters={filters}
          storeDrawerOpen={storeDrawerOpen}
          onItemClick={(item) => {
            setSelectedStoreItem(item);
          }}
          permissions={permissions}
          onAddStoreItem={() => {
            setOpenAddStoreItem(true);
          }}
          onAddStoreProduct={() => {
            setOpenAddStoreProduct(true);
          }}
          deleteItem={() => {
            deleteStoreItem();
          }}
          onItemsLoaded={onItemsLoaded}
        />
      </div>
    );
  };

  return (
    <div>
      {renderBody()}
      <ArchivedStoreItemsDialog
        open={openArchives}
        permissions={permissions}
        store={selectedStore}
        onRemoveStoreItem={() => {
          fetchStores();
        }}
        onRestoreStoreItem={() => {
          fetchStores();
        }}
        onClose={() => {
          setOpenArchives(false);
        }}
      />
      {openAuditStoreDialog && (
        <AuditStoreDialog
          store={selectedStore}
          open={openAuditStoreDialog}
          onClose={() => {
            setOpenAuditStoreDialog(false);
          }}
        />
      )}
      {openScanQrCode && (
        <ScanQrCode
          open={openScanQrCode}
          storeItems={storeItems}
          openItem={(item) => {
            setSelectedStoreItem(item);
          }}
          onClose={() => {
            setOpenScanQrCode(false);
          }}
        />
      )}

      <StoreDrawer
        hideFab={hideDrawerFab}
        selectedStore={selectedStore}
        onAddItem={() => {
          setOpenAddStoreItem(true);
        }}
        permissions={permissions}
        onAuditStore={() => {
          setOpenAuditStoreDialog(true);
        }}
        onOpen={() => {
          setStoreDrawerOpen(true);
        }}
        onClose={() => {
          setStoreDrawerOpen(false);
        }}
        onOpenArchives={() => {
          setOpenArchives(true);
        }}
        onExportToCsv={() => {
          setOpenExportStoreItems(true);
        }}
        onGenerateLabels={() => {
          setOpenStoreLabelsDialog(true);
        }}
        onAnalytics={onAnalytics}
        onEditStore={onEditStore}
        onArchiveStore={onArchiveStore}
      />
      {openAddStoreItem && (
        <ItemTypesDialog
          onAdd={async () => {
            fetchStores();
          }}
          permissions={permissions}
          onItemTypeDelete={async (cb) => {
            await fetchItemTypes();
            if (typeof cb === 'function') {
              cb();
            }
            fetchStores();
          }}
          onItemTypeUpdate={async (cb) => {
            await fetchItemTypes();

            if (typeof cb === 'function') {
              cb();
            }
          }}
          onItemTypeCreate={async (cb) => {
            await fetchItemTypes();
            if (typeof cb === 'function') {
              cb();
            }
          }}
          storeItems={storeItems}
          open={openAddStoreItem}
          store={selectedStore}
          onClose={() => {
            setOpenAddStoreItem(false);
          }}
        />
      )}
      {openAddStoreProduct && (
        <AddStoreProductDialog
          open={openAddStoreProduct}
          onProductAdd={async () => {
            toast.success('Product successfully added.');
            setOpenAddStoreProduct(false);

            // this will cause store items to refresh
            // store items list has useEffect on the selected store
            // which will get refresh due to this call
            fetchStores();
          }}
          storeItems={storeItems}
          store={selectedStore}
          onClose={() => {
            setOpenAddStoreProduct(false);
          }}
        />
      )}
      {openExportStoreItems && (
        <GenerateReportsDialog
          store={selectedStore}
          open={openExportStoreItems}
          onClose={() => {
            setOpenExportStoreItems(false);
          }}
        />
      )}
      {openStoreLabelsDialog && (
        <StoresLabelDialog
          store={selectedStore}
          open={openStoreLabelsDialog}
          onClose={() => {
            setOpenStoreLabelsDialog(false);
          }}
        />
      )}
    </div>
  );
}
Stores.propTypes = {};

Stores.defaultProps = {};
export default Stores;
